import api from './api';
import type { Customer, CustomerCreate, CustomerUpdate, CustomerSearchResponse, CustomerSelectOption, CustomerOrderResponse } from '@/types/customer';

const customerService = {
  // Create a new customer
  async createCustomer(customerData: CustomerCreate): Promise<Customer> {
    try {
      const response = await api.post('/customers/', customerData);
      
      // Axios interceptor flattens the response, so response is already the customer object
      const customer = response as unknown as Customer;
      
      if (!customer || !customer.id) {
        throw new Error('Invalid customer response: missing id');
      }
      
      return customer;
    } catch (error) {
      console.error('Error in createCustomer:', error);
      throw error;
    }
  },

  // Get customer by ID
  async getCustomer(customerId: number): Promise<Customer> {
    const response = await api.get(`/customers/${customerId}`);
    return response.data;
  },

  // Search customers by name or phone
  async searchCustomers(query?: string, limit: number = 50, offset: number = 0): Promise<CustomerSearchResponse> {
    const params = new URLSearchParams();
    if (query) params.append('q', query);
    params.append('limit', limit.toString());
    params.append('offset', offset.toString());
    
    const response = await api.get(`/customers/search/?${params.toString()}`);
    // Axios interceptor flattens the response, so response is already the CustomerSearchResponse
    return response as unknown as CustomerSearchResponse;
  },

  // Get customers by phone (exact match)
  async getCustomersByPhone(phone: string): Promise<Customer[]> {
    const response = await api.get(`/customers/phone/${encodeURIComponent(phone)}`);
    return response.data;
  },

  // Update customer
  async updateCustomer(customerId: number, customerData: CustomerUpdate): Promise<Customer> {
    const response = await api.put(`/customers/${customerId}`, customerData);
    return response.data;
  },

  // Delete customer (soft delete)
  async deleteCustomer(customerId: number): Promise<void> {
    await api.delete(`/customers/${customerId}`);
  },

  // Get customer orders
  async getCustomerOrders(customerId: number): Promise<CustomerOrderResponse> {
    const response = await api.get(`/customers/${customerId}/orders`);
    return response.data;
  },

  // Get frequent customers
  async getFrequentCustomers(limit: number = 20): Promise<Customer[]> {
    const response = await api.get(`/customers/frequent/?limit=${limit}`);
    return response.data;
  },

  // Get recent customers
  async getRecentCustomers(limit: number = 20): Promise<Customer[]> {
    const response = await api.get(`/customers/recent/?limit=${limit}`);
    return response.data;
  },

  // Quick search for customer selection (returns limited data)
  async quickSearch(query?: string, limit: number = 10): Promise<CustomerSelectOption[]> {
    const customers = await this.searchCustomers(query, limit, 0);
    return customers.customers.map(customer => ({
      id: customer.id,
      display_name: customer.display_name,
      phone: customer.phone,
      full_address: customer.full_address,
      order_count: customer.order_count
    }));
  }
};

export default customerService;
